import React, { useState } from 'react';
import { ExternalLinkIcon, FlameIcon, InboxIcon, RefreshCwIcon } from 'lucide-react';
import { HotLeadRow } from './HotLeadRow';
import { formatRelativeTime } from '../../utils/format';
import { useHotLeads } from '../../contexts/HotLeadContext';
type LeadFilter = 'pending' | 'reviewed';
interface HotLeadListProps {
  /** Manager working the queue. */
  reviewerName?: string;
  onOpenFullList?: () => void;
}
export function HotLeadList({
  reviewerName = 'Levi',
  onOpenFullList
}: HotLeadListProps) {
  const {
    pending,
    reviewed,
    urgentCount,
    agreementRate,
    lastSyncedAt,
    isSyncing,
    refresh,
    agree,
    override
  } = useHotLeads();
  const [filter, setFilter] = useState<LeadFilter>('pending');
  const [overridingId, setOverridingId] = useState<string | null>(null);
  const leads = filter === 'pending' ? pending : reviewed;
  return <section className="flex h-full w-full flex-col overflow-hidden rounded-t-lg border border-slate-300 bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.15)]" aria-label="Hot lead list" data-id="element-24" data-mp="1vtolcfd9vn06">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-[#0176d3] px-4 py-2.5 text-white" data-id="element-25" data-mp="7hvm6tpan10f">
        <div className="flex items-center gap-2" data-id="element-26" data-mp="lftupigxo380">
          <FlameIcon className="h-4 w-4" aria-hidden="true" data-id="element-27" data-mp="gia89va30k4z" />
          <h2 className="text-sm font-semibold" data-id="element-28" data-mp="gitq7bzqf8j0">Hot Lead List</h2>
          {urgentCount > 0 && <span className="rounded-full bg-[#ba0517] px-2 py-0.5 text-[11px] font-bold" aria-label={`${urgentCount} urgent leads`} data-id="element-29" data-mp="3oy3nm6sofpkg">
              {urgentCount}
            </span>}
        </div>
        <div className="flex items-center gap-3 text-[11px]" data-id="element-30" data-mp="3twezen2j8a5b">
          <span className="flex items-center gap-1.5" data-id="element-31" data-mp="3tuu610ryex91">
            <span className={`h-1.5 w-1.5 rounded-full ${isSyncing ? 'bg-amber-300' : 'bg-emerald-300'}`} aria-hidden="true" data-id="element-32" data-mp="366awkhx64efd" />
            Live · synced {formatRelativeTime(lastSyncedAt.toISOString())}
          </span>
          <button type="button" onClick={refresh} className="rounded p-1 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Refresh feed" data-id="element-33" data-mp="1a47tg8g34h50">
            <RefreshCwIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-34" data-mp="1jszhi5i0yhan" />
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2" data-id="element-35" data-mp="r6eqx79di985">
        <div className="flex items-center gap-1" role="tablist" aria-label="Review queue filter" data-id="element-36" data-mp="w2vfu3oecrma">
          <FilterTab isActive={filter === 'pending'} onClick={() => setFilter('pending')} label="Pending review" count={pending.length} data-id="element-37" data-mp="27jlnzscdrtij" />
          <FilterTab isActive={filter === 'reviewed'} onClick={() => setFilter('reviewed')} label="Reviewed" count={reviewed.length} data-id="element-38" data-mp="3w37zvzc1qkwn" />
        </div>
        <p className="text-[11px] text-slate-600" data-id="element-39" data-mp="3naxls6y15va4">
          {reviewerName}&rsquo;s agreement rate:{' '}
          <span className="font-semibold text-slate-900" data-id="element-40" data-mp="3ic2u0fi34wdv">{agreementRate}%</span>
          <span className="text-slate-500" data-id="element-41" data-mp="3ia05y10htdbs"> · autonomy at 90%</span>
        </p>
      </div>

      {leads.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center" data-id="element-42" data-mp="38d5bpub95z3b">
          <InboxIcon className="h-6 w-6 text-slate-400" aria-hidden="true" data-id="element-43" data-mp="34bjr1msh8gik" />
          <p className="text-sm font-medium text-slate-700" data-id="element-44" data-mp="2zcyr7oh4zyun">
            {filter === 'pending' ? 'Queue is clear' : 'Nothing reviewed yet'}
          </p>
          <p className="text-xs text-slate-500" data-id="element-45" data-mp="12k6nb128weo3">
            {filter === 'pending' ? 'New hot leads appear here within about 5 minutes of being flagged.' : 'Agreed and overridden leads will collect here.'}
          </p>
        </div> : <ul className="flex-1 overflow-y-auto" data-id="element-46" data-mp="3vffa7jbt8zd6">
          {leads.map(lead => <HotLeadRow key={lead.id} lead={lead} isOverriding={overridingId === lead.id} onStartOverride={() => setOverridingId(lead.id)} onCancelOverride={() => setOverridingId(null)} onAgree={() => agree(lead.id)} onOverride={(code, subcode) => {
        override(lead.id, code, subcode);
        setOverridingId(null);
      }} data-id="element-47" data-mp="3hfnw7ilaniz6" />)}
        </ul>}

      <footer className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2" data-id="element-48" data-mp="32q886ad5drn6">
        <p className="text-[11px] text-slate-500" data-id="element-49" data-mp="2oq71ufe45s0b">Feed runs on a ~5 minute lag</p>
        <button type="button" onClick={onOpenFullList} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-50" data-mp="2tpjx9oj1w7ei">
          Open full hot lead list
          <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-51" data-mp="2k27hhykdil6h" />
        </button>
      </footer>
    </section>;
}
interface FilterTabProps {
  isActive: boolean;
  onClick: () => void;
  label: string;
  count: number;
}
function FilterTab({
  isActive,
  onClick,
  label,
  count
}: FilterTabProps) {
  return <button type="button" role="tab" aria-selected={isActive} onClick={onClick} className={`rounded px-2.5 py-1 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] ${isActive ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-300' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-52" data-mp="2o4ccb0mk5nup">
      {label}
      <span className="ml-1.5 text-slate-500" data-id="element-53" data-mp="37sbma99asqz2">{count}</span>
    </button>;
}